/**
 * The Kolor marki tab: six ready-made colours, one custom picker, and the button that saves.
 *
 * The colour is the trainer's clients' accent, so nothing on this screen is about this screen —
 * what it is about stands in the column beside it, on a phone. Picking repaints that phone at once
 * and commits nothing; Zapisz commits.
 *
 * ── The tiles are a radio group with ONE tab stop ───────────────────────────────────────────
 * Six controls that are six answers to one question are a radio group, and the pattern's rule is a
 * roving tabindex: Tab reaches the group, the arrows move inside it. Six separate tab stops in a
 * 3x2 grid is what a row of `<button>`s would have cost, and the keyboard user pays it on every
 * pass through the screen. The arrows wrap, because a grid of swatches has no beginning.
 *
 * ── The AA chip is a MEASUREMENT ────────────────────────────────────────────────────────────
 * It reports what the colour the PICKER is standing on would give a client — `shared/lib/contrast`
 * moves the fill under a white label until it clears 4.5:1, and this is that number. By construction it
 * always clears, which is exactly what makes the chip a regression detector: it disappears rather
 * than lies the day the adjustment stops holding. Nothing is refused on contrast — any hex saves.
 *
 * ── Zapisz is the header primary WITH a word in it ──────────────────────────────────────────
 * `shared/ui/emberButton` for the treatment, this stylesheet for the geometry — 36px, radius 12,
 * 12.5/800, a 19px tick — stated here rather than as three overrides of `screenHeader`'s `.primary`,
 * which is a class belonging to another surface.
 */

import { useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { BRAND_PRESETS, brandPaint, type BrandPresetId } from '~/entities/brand-color';
import { AA_TEXT_RATIO } from '~/shared/lib/contrast';
import { CheckIcon } from '~/shared/ui/AuthIcons';
import { FormBanner } from '~/shared/ui/FormBanner';
import chip from '~/shared/ui/neutralChip.module.css';
import ember from '~/shared/ui/emberButton.module.css';
import ghost from '~/shared/ui/ghostButton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { AccountBrandColorApi } from '../lib/useBrandColor';
import { ColorPicker } from './ColorPicker';
import screen from './AccountScreen.module.css';
import styles from './BrandColorTab.module.css';

interface BrandColorTabProps {
  readonly brand: AccountBrandColorApi;
}

/**
 * What each tile paints, derived ONCE for the module. The six presets are a fixed table rather than
 * an answer, so re-walking the contrast maths per tile per render bought nothing.
 */
const PRESET_PAINT = new Map<BrandPresetId, string>(
  BRAND_PRESETS.map((preset) => [preset.id, brandPaint({ kind: 'preset', preset: preset.id }).fill]),
);

/** One decimal, with the reader's own decimal mark — „8,4" in Polish, „8.4" in English. */
function ratioText(ratio: number, locale: string): string {
  return ratio.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

export function BrandColorTab({ brand }: BrandColorTabProps) {
  const { t, i18n } = useTranslation();
  const tiles = useRef<(HTMLButtonElement | null)[]>([]);
  const refusalId = useId();

  /* The ring is withheld for the whole WAIT, and that IS this block's loading state: the six
   * colours are a fixed palette rather than an answer, so drawing bars over them would replace
   * known content with a guess about it. What is genuinely unknown is WHICH one is the trainer's,
   * and the phone beside this — which draws nothing but the answer — is where the skeleton goes.
   *
   * On `waiting` rather than on the read, so the ring and the phone's own hand-over are the same
   * moment. The read answers up to 300ms before the gate retires the phone's bars. */
  const selectedIndex =
    !brand.waiting && brand.state === 'ready'
      ? BRAND_PRESETS.findIndex(
          (preset) => brand.selection.kind === 'preset' && brand.selection.preset === preset.id,
        )
      : -1;
  /** Where the roving tab stop sits. With a custom colour picked, no tile is checked and the first
   * one carries it — the group still has to be reachable. */
  const focusIndex = selectedIndex === -1 ? 0 : selectedIndex;

  const move = (from: number, delta: number) => {
    const next = (from + delta + BRAND_PRESETS.length) % BRAND_PRESETS.length;
    const preset = BRAND_PRESETS[next];
    if (preset === undefined) return;
    // The radio-group pattern: moving focus inside the group MOVES the choice with it.
    brand.onPickPreset(preset.id);
    tiles.current[next]?.focus();
  };

  const onTileKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    // Three columns, so up and down are a row apart and left and right are one tile.
    const deltas: Readonly<Record<string, number>> = {
      ArrowRight: 1,
      ArrowDown: 3,
      ArrowLeft: -1,
      ArrowUp: -3,
    };
    const delta = deltas[event.key];
    if (delta === undefined) return;
    event.preventDefault();
    move(index, delta);
  };

  // The failure is content too, and it waits with everything else this tab draws from the read.
  const failed = !brand.waiting && brand.state === 'error';
  const ratio = ratioText(brand.customRatio, i18n.language);
  /* A half-typed hex leaves the LAST whole colour as the selection; saving would store that one
   * under a field that says something else. */
  const savable = brand.isDirty && !brand.hexIncomplete;

  return (
    <div className={screen.inner}>
      <div className={screen.lede}>
        <h2 className={screen.h2}>{t('profile.brandColor.title')}</h2>
        <p className={screen.ledeText}>{t('profile.brandColor.lede')}</p>
      </div>

      {failed && (
        <div className={screen.block}>
          <FormBanner message={t('profile.brandColor.loadErrorTitle')} />
          <button type="button" className={`${String(ghost.ghost)} ${String(screen.accountButton)} ${String(styles.retry)}`} onClick={brand.onRetry}>
            {t('common.retry')}
          </button>
        </div>
      )}

      {/* `aria-busy` and one hidden sentence are what a listener gets for the length of the WAIT
        * — the same value the ring and the failure above fork on, so nothing here calls the tab
        * settled while another part of it is still waiting. The tiles themselves ARE their own
        * reserve, laid out identically in all three states, so only the ring moves. */}
      <div className={screen.block} aria-busy={brand.waiting}>
        <h3 className={screen.blockTitle}>{t('web:account.brandColor.presetsTitle')}</h3>
        {brand.waiting && (
          <span className={hidden.srOnly}>{t('web:account.brandColor.waiting')}</span>
        )}

        <div className={styles.tiles} role="radiogroup" aria-label={t('web:account.brandColor.presetsTitle')}>
          {BRAND_PRESETS.map((preset, index) => {
            const checked = index === selectedIndex;
            const name = t(preset.labelKey);
            return (
              <button
                key={preset.id}
                ref={(node) => {
                  tiles.current[index] = node;
                }}
                type="button"
                role="radio"
                aria-checked={checked}
                // The ONE tab stop — see the header.
                tabIndex={index === focusIndex ? 0 : -1}
                className={`${String(styles.tile)} ${checked ? String(styles.tileOn) : ''}`}
                onKeyDown={(event) => {
                  onTileKeyDown(event, index);
                }}
                onClick={() => {
                  brand.onPickPreset(preset.id);
                }}
              >
                <span
                  className={styles.swatch}
                  style={{ background: PRESET_PAINT.get(preset.id) }}
                  aria-hidden="true"
                />
                {name}
              </button>
            );
          })}
        </div>
      </div>

      <div className={screen.block}>
        <h3 className={screen.blockTitle}>
          {t('profile.brandColor.customTitle')}
          <small className={styles.blockNote}>{t('profile.brandColor.customSubtitle')}</small>
        </h3>

        <div className={styles.custom}>
          <ColorPicker
            hsv={brand.hsv}
            onPlaneChange={brand.onPlaneChange}
            onHueChange={brand.onHueChange}
          />

          <div className={styles.side}>
            <label className={styles.hexField}>
              <span className={styles.hexHash} aria-hidden="true">
                #
              </span>
              <input
                className={styles.hexInput}
                value={brand.hexText}
                onChange={(event) => {
                  brand.onHexChange(event.target.value);
                }}
                // Six digits and no `#`: the prefix is drawn beside the field, as the mockup draws
                // it, so a pasted `#e0356b` still lands as six digits via `normaliseHexInput`.
                maxLength={7}
                inputMode="text"
                autoComplete="off"
                spellCheck={false}
                aria-label={t('profile.brandColor.hexA11y')}
              />
            </label>

            {/* It is a MEASUREMENT: absent rather than wrong if the floor is ever missed, and absent
              * while the field holds no whole colour — a ratio for a half-typed hex is a number
              * about the previous colour, under a field that no longer says it. */}
            {!brand.hexIncomplete && brand.customRatio >= AA_TEXT_RATIO && (
              <span
                className={`${String(chip.neutralChip)} ${String(styles.aa)}`}
                // The words on screen are shorthand — „Kontrast AA · 8,4:1" — and the label is the
                // sentence, which is what SC 2.5.3 asks of a name that has to contain its label.
                aria-label={t('profile.brandColor.contrastA11y', { ratio })}
              >
                <CheckIcon className={styles.aaIcon} />
                {t('web:account.brandColor.contrastChip', {
                  label: t('profile.brandColor.contrastBadge'),
                  ratio,
                })}
              </span>
            )}
            {/* The ratio is the MOVED fill's; the field still says the picked hex. Said in words, so
              * the trainer is never shown one colour and their clients another. */}
            {!brand.hexIncomplete && brand.adjustedHex !== null && (
              <p className={styles.adjusted}>
                {t('web:account.brandColor.adjusted', { hex: brand.adjustedHex.replace('#', '') })}
              </p>
            )}

            <button
              type="button"
              className={`${String(ember.ember)} ${String(styles.save)}`}
              aria-disabled={savable && !brand.saving ? undefined : 'true'}
              aria-describedby={savable ? undefined : refusalId}
              onClick={savable && !brand.saving ? brand.onSave : undefined}
            >
              <CheckIcon className={styles.saveIcon} />
              {t('web:account.brandColor.save')}
            </button>
            {/* A half-typed hex says so where it can be read; an unchanged colour only to a reader. */}
            {brand.hexIncomplete ? (
              <span id={refusalId} className={styles.adjusted}>
                {t('web:account.brandColor.hexIncomplete')}
              </span>
            ) : !brand.isDirty ? (
              <span id={refusalId} className={hidden.srOnly}>
                {t('web:account.brandColor.saveRefusal')}
              </span>
            ) : null}
          </div>
        </div>

        <FormBanner message={brand.saveError} />
      </div>
    </div>
  );
}
